import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Diagnostics } from "../../../../../../services";
import Panel, { EmptyState, PanelTitle } from "../../components/Panel";
import DataTable, {
	Accent,
	ClickableRow,
	TableScroll,
} from "../../components/DataTable";
import { useSystemPerformance } from "../../context";
import useRequest from "../../hooks/useRequest";
import { formatBytes, formatDateTime } from "../../utils/format";
import ServiceOperationMark from "../../components/ServiceOperationMark";

import IncidentDetails from "./IncidentDetails";

const Root = styled.div`
	display: grid;
	grid-template-columns: minmax(420px, 1fr) minmax(0, 1.2fr);
	gap: 16px;

	height: 100%;
	min-height: 0;
`;

const ListPanel = styled(Panel)`
	min-height: 0;
	overflow: hidden;
`;

/**
 * Incident list on the left, the selected incident card on the right.
 *
 * Investigating an incident almost always means comparing it with the
 * neighbouring ones, so the list stays on screen instead of hiding behind a
 * modal window.
 */
const Incidents: React.FC = () => {
	const { t } = useTranslation();
	const { rangeMinutes, refreshToken } = useSystemPerformance();

	const [selected, setSelected] = useState<string | null>(null);

	const { data, loading } = useRequest<Diagnostics.IncidentsResponse>(
		() =>
			Diagnostics.getIncidents({
				// Все сервисы сразу: фильтр по одному микросервису прятал
				// соседние инциденты, а сервис и так виден в колонке.
				from: Date.now() - Math.max(rangeMinutes, 1440) * 60_000,
				to: Date.now(),
				limit: 100,
			}),
		[rangeMinutes, refreshToken],
	);

	useEffect(() => {
		const items = data?.items ?? [];

		if (!items.length) {
			setSelected(null);
			return;
		}

		if (!items.some((item) => item.id === selected)) {
			setSelected(items[0].id);
		}
	}, [data, selected]);

	return (
		<Root>
			<ListPanel>
				<PanelTitle>
					{t("settings.systemPerformance.incidents.title")}
				</PanelTitle>

				{data?.items?.length ? (
					<TableScroll>
						<DataTable>
							<thead>
								<tr>
									<th>
										{t(
											"settings.systemPerformance.incidents.time",
										)}
									</th>
									<th>
										{t(
											"settings.systemPerformance.filters.service",
										)}
									</th>
									<th>
										{t(
											"settings.systemPerformance.incidents.reason",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.incidents.heapUsed",
										)}
									</th>
									<th>
										{t(
											"settings.systemPerformance.incidents.topOperation",
										)}
									</th>
								</tr>
							</thead>
							<tbody>
								{data.items.map((item) => (
									<ClickableRow
										key={item.id}
										active={item.id === selected}
										onClick={() => setSelected(item.id)}
									>
										<td>{formatDateTime(item.ts)}</td>
										<td>{item.service}</td>
										<td>
											<Accent
												tone={
													item.reason === "threshold"
														? "danger"
														: "warning"
												}
											>
												{t(
													`settings.systemPerformance.incidents.reasons.${item.reason}`,
												)}
											</Accent>
										</td>
										<td className="numeric">
											{formatBytes(item.heapUsed)}
										</td>
										<td className="wide">
											{item.topOperation ?? "—"}
											<ServiceOperationMark
												name={item.topOperation}
											/>
										</td>
									</ClickableRow>
								))}
							</tbody>
						</DataTable>
					</TableScroll>
				) : (
					<EmptyState>
						{loading
							? "…"
							: t("settings.systemPerformance.incidents.empty")}
					</EmptyState>
				)}
			</ListPanel>

			{selected ? <IncidentDetails id={selected} /> : <div />}
		</Root>
	);
};

export default Incidents;
